import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, Shield, TrendingUp, Lock, ClipboardCheck, GraduationCap } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navItems, companyInfo } from '../data/mock';
import { Button } from './ui/button';

const serviceDropdownItems = [
  { 
    label: 'ISO Services', 
    icon: Shield,
    color: 'text-blue-600',
    href: '/#iso-services',
    isRoute: false
  },
  { 
    label: 'CMMI Services', 
    icon: TrendingUp,
    color: 'text-green-500',
    href: '/#cmmi-services',
    isRoute: false
  },
  { 
    label: 'CMMC Services', 
    icon: Lock,
    color: 'text-blue-500',
    href: '/#cmmc-services',
    isRoute: false
  },
  { 
    label: 'SOC 2 Services', 
    icon: ClipboardCheck,
    color: 'text-green-600',
    href: '/#soc2-services',
    isRoute: false
  },
  { 
    label: 'Training Services', 
    icon: GraduationCap,
    color: 'text-blue-700',
    href: '/#training-services',
    isRoute: false
  }
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle scroll to anchor elements
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const id = hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 0);
    }
  }, [location]);

  const handleNavClick = (href, isRoute = false) => {
    setIsOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
    
    if (isRoute) {
      // Route navigation is handled by Link component
      return;
    }
    
    // Extract hash from href (handle both '#id' and '/#id' formats)
    const hash = href.includes('#') ? href.substring(href.indexOf('#')) : href;
    
    // Hash anchor navigation
    // If on a different page, navigate to home with anchor
    if (location.pathname !== '/' && hash.startsWith('#')) {
      navigate('/' + hash);
    } else if (hash.startsWith('#')) {
      // On home page, scroll directly to anchor
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const filteredNavItems = navItems.filter(item => item.label !== 'Services' && item.label !== 'Contact');

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button onClick={() => handleNavClick('#home')} className="flex items-center">
              <img 
                src="/logo.png" 
                alt="ANTS Consulting" 
                className="h-12 w-auto"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {/* Home Link */}
            <button
              onClick={() => handleNavClick('#home')}
              className="px-4 py-2 rounded-lg text-base font-semibold transition-all duration-200 hover:bg-blue-500/10 text-slate-950 hover:text-blue-600"
            >
              Home
            </button>

            {/* Services Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                onMouseEnter={() => setServicesOpen(true)}
                className="px-4 py-2 rounded-lg text-base font-semibold transition-all duration-200 hover:bg-blue-500/10 flex items-center gap-1 text-slate-950 hover:text-blue-600"
              >
                Services
                <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              <div
                onMouseLeave={() => setServicesOpen(false)}
                className={`absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden transition-all duration-200 ${
                  servicesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                }`}
              >
                <div className="p-2">
                  {serviceDropdownItems.map((item, index) => {
                    const IconComponent = item.icon;
                    
                    if (item.isRoute) {
                      return (
                        <Link
                          key={index}
                          to={item.href}
                          onClick={() => setServicesOpen(false)}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left group no-underline"
                        >
                          <div className={`w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-blue-50 transition-colors`}>
                            <IconComponent className={`w-5 h-5 ${item.color}`} strokeWidth={2} />
                          </div>
                          <div className="text-base font-semibold text-slate-950 group-hover:text-blue-600 transition-colors">
                            {item.label}
                          </div>
                        </Link>
                      );
                    }
                    
                    return (
                      <button
                        key={index}
                        onClick={() => handleNavClick(item.href)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left group"
                      >
                        <div className={`w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-blue-50 transition-colors`}>
                          <IconComponent className={`w-5 h-5 ${item.color}`} strokeWidth={2} />
                        </div>
                        <div className="text-base font-semibold text-slate-950 group-hover:text-blue-600 transition-colors">
                          {item.label}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Other Nav Items */}
            {filteredNavItems.slice(1).map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item.href)}
                className="px-4 py-2 rounded-lg text-base font-semibold transition-all duration-200 hover:bg-blue-500/10 text-slate-950 hover:text-blue-600"
              >
                {item.label}
              </button>
            ))}
            <Button
              onClick={() => handleNavClick('#contact')}
              className="ml-4 bg-emerald-600 hover:bg-emerald-700 text-white px-6"
            >
              Contact Us
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg transition-colors text-slate-900"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className={`lg:hidden transition-all duration-300 overflow-hidden ${
          isOpen ? 'max-h-screen' : 'max-h-0'
        }`}
      >
        <div className="bg-white/95 backdrop-blur-md border-t border-slate-200 px-4 py-4 space-y-2">
          {/* Home */}
          <button
            onClick={() => handleNavClick('#home')}
            className="block w-full text-left px-4 py-3 rounded-lg text-base font-semibold text-slate-950 hover:bg-blue-50 hover:text-blue-600 transition-colors"
          >
            Home
          </button>

          {/* Services with submenu */}
          <div>
            <button
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              className="flex items-center justify-between w-full text-left px-4 py-3 rounded-lg text-base font-semibold text-slate-950 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            >
              <span>Services</span>
              <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Mobile Services Submenu */}
            <div className={`overflow-hidden transition-all duration-300 ${mobileServicesOpen ? 'max-h-96' : 'max-h-0'}`}>
              <div className="pl-4 py-2 space-y-1">
                {serviceDropdownItems.map((item, index) => {
                  const IconComponent = item.icon;
                  
                  if (item.isRoute) {
                    return (
                      <Link
                        key={index}
                        to={item.href}
                        onClick={() => {
                          setMobileServicesOpen(false);
                          setIsOpen(false);
                        }}
                        className="flex items-center gap-3 w-full text-left px-4 py-3 rounded-lg text-slate-950 hover:bg-blue-50 hover:text-blue-600 transition-colors no-underline font-semibold"
                      >
                        <IconComponent className={`w-5 h-5 ${item.color}`} />
                        {item.label}
                      </Link>
                    );
                  }
                  
                  return (
                    <button
                      key={index}
                      onClick={() => handleNavClick(item.href)}
                      className="flex items-center gap-3 w-full text-left px-4 py-2 rounded-lg text-slate-950 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      <IconComponent className={`w-5 h-5 ${item.color}`} />
                      <span className="text-base font-semibold">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Other nav items */}
          {filteredNavItems.slice(1).map((item) => (
            <button
              key={item.label}
              onClick={() => handleNavClick(item.href)}
              className="block w-full text-left px-4 py-3 rounded-lg text-base font-semibold text-slate-950 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            >
              {item.label}
            </button>
          ))}
          <Button
            onClick={() => handleNavClick('#contact')}
            className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Contact Us
          </Button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
